import type { FC } from 'react';
import { useMemo } from 'react';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import { SecuritySearchableFilter } from './SecuritySearchableFilter';
import { useWazuhEndpointFilterOptions } from './useWazuhEndpointFilterOptions';
import { buildEndpointsApiScope } from './wazuhScopeParams';

const DEFAULT_PILL_LABEL = 'Endpoints';
const DEFAULT_ALL_LABEL = 'All Endpoints';
const DEFAULT_SEARCH_PLACEHOLDER = 'Search endpoints...';
const DEFAULT_EMPTY_MESSAGE = 'No endpoints found';
const DEFAULT_SELECTED_FALLBACK = 'Selected endpoint';

export type SecurityEndpointFilterProps = {
  value: string | null;
  onChange: (endpointId: string | null) => void;
  /** Page-local Organization filter (reseller); narrows endpoint options. */
  pageOrganizationId?: string;
  pillLabel?: string;
  allLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectedFallbackLabel?: string;
  showFilterIcon?: boolean;
  fullWidth?: boolean;
};

/**
 * Searchable endpoints filter for all roles.
 * Options from GET /api/v1/endpoints via useWazuhEndpointFilterOptions + buildEndpointsApiScope.
 */
export const SecurityEndpointFilter: FC<SecurityEndpointFilterProps> = ({
  value,
  onChange,
  pageOrganizationId,
  pillLabel = DEFAULT_PILL_LABEL,
  allLabel = DEFAULT_ALL_LABEL,
  searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  selectedFallbackLabel = DEFAULT_SELECTED_FALLBACK,
  showFilterIcon = false,
  fullWidth = false,
}) => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;

  const listScope = useMemo(
    () =>
      buildEndpointsApiScope({
        scope,
        profileOrgId,
        isReseller,
        pageOrganizationId,
      }),
    [scope, profileOrgId, isReseller, pageOrganizationId]
  );

  const { options, loading, onSearch } = useWazuhEndpointFilterOptions(listScope);

  return (
    <SecuritySearchableFilter
      value={value}
      onChange={onChange}
      options={options}
      onSearch={onSearch}
      loading={loading}
      pillLabel={pillLabel}
      allLabel={allLabel}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      selectedFallbackLabel={selectedFallbackLabel}
      showFilterIcon={showFilterIcon}
      fullWidth={fullWidth}
    />
  );
};
